import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { get } from 'lodash-es';

import { Menu } from '@shared/models/menu';
import { MenuModel } from '@shared/models/menu-model';

import { MembershipService } from '@core/services/membership/membership.service';
import { PriceService } from '@core/services/price/price.service';
import { GtmService } from '@core/services/analytics/gtm.service';
import { Outlet, OutletState } from '@app/shared/models/api-response/catalog-api-response';

@Injectable({
    providedIn: 'root'
})
export class MenuService {
    constructor(
        private membershipService: MembershipService,
        private priceService: PriceService,
        private gtmService: GtmService
    ) {}

    state = new BehaviorSubject<Menu>(new Menu());
    menuModel = new BehaviorSubject<MenuModel>(new MenuModel());

    hydrateState(data) {
        this.state.next(data); // pushing data to behaviour subject
    }

    hydrateMenuModel(data) {
        this.menuModel.next(data); // pushing data to behaviour subject
    }

    arrayToHash(array) {
        const hash = {};
        array.forEach(entity => {
            hash[entity.id] = entity;
        });
        return hash;
    }

    isServiceable(outlet: Outlet) {
        if (!outlet) {
            return false;
        }
        if (outlet.dflt) {
            return false;
        } else if (!outlet.active) {
            return false;
        }

        return true;
    }

    parseOutletData(outlet: Outlet) {
        if (!outlet.id) {
            return;
        }

        const currentMenuModel = this.menuModel.value;
        if (currentMenuModel.menu_id === outlet.menu_id && currentMenuModel.active && currentMenuModel.open) {
            this.priceService.setMenu(currentMenuModel);
            return;
        }

        const { menu, menuModel } = this.generateMenuModel(outlet);
        this.priceService.setMenu(menuModel);

        this.hydrateMenuModel(menuModel);
        this.hydrateState(menu);
    }

    generateMenuModel(outlet: Outlet) {
        const menuModel = {
            outlet_id: outlet.id,
            menu_id: outlet.menu_id,
            active: outlet.active,
            open: outlet.open,
            brand_id: outlet.brand_id,
            brand_name: this.membershipService.getBrandName(outlet) || outlet.brand_name,
            product_categories: this.arrayToHash(outlet.product_categories),
            product_sizes: this.arrayToHash(outlet.product_sizes),
            product_customization_types: {},
            product_customizations: this.arrayToHash(outlet.product_customizations),
            ct_groups: this.arrayToHash(outlet.ct_groups) || {},
            product_t_config: outlet.product_t_config || {},
            products: {},
            menu: {},
            outlet_state: outlet.outlet_state,
            max_total_quantity: 99
        };

        const productCustomizationsHash = {};
        outlet.product_customizations.forEach(entity => {
            productCustomizationsHash[entity.product_customization_type_id] = [
                ...(productCustomizationsHash[entity.product_customization_type_id] || []),
                entity
            ];
        });

        const sizeToCustomizationTypes = {};

        outlet.product_customization_types.forEach(customization_type => {
            customization_type.product_size_ids.forEach(id => {
                if (!sizeToCustomizationTypes[id]) {
                    sizeToCustomizationTypes[id] = [];
                }
                sizeToCustomizationTypes[id].push(customization_type);
            });
        });

        menuModel.product_customization_types = this.arrayToHash(
            outlet.product_customization_types.map(productCustomizationType => {
                const pct = { ...productCustomizationType };
                pct.customizations = productCustomizationsHash[pct.id];
                pct.sold_out = pct.customizations.reduce((prev, customization) => {
                    return prev && customization.sold_out;
                }, true);

                return pct;
            })
        );

        const customizationTypeIdsOfProductSize = {};

        outlet.product_customization_types.forEach(pct => {
            pct.product_size_ids.forEach(id => {
                if (!customizationTypeIdsOfProductSize[id]) {
                    customizationTypeIdsOfProductSize[id] = [];
                }

                customizationTypeIdsOfProductSize[id].push(menuModel.product_customization_types[pct.id]);
            });
        });

        const productSizesHash = {};
        outlet.product_sizes.forEach(entity => {
            entity.product_customization_types = customizationTypeIdsOfProductSize[entity.id]
                ? customizationTypeIdsOfProductSize[entity.id]
                : [];
            productSizesHash[entity.product_id] = [...(productSizesHash[entity.product_id] || []), entity];
        });

        const menu = {
            data: outlet.products.map(product => ({ ...product, product_sizes: productSizesHash[product.id] })),
            sequence: outlet.product_categories.filter((category: any) => category.saleable && !category.is_subscribable)
        };

        menuModel.products = this.arrayToHash(menu.data);
        menuModel.menu = menu;

        return { menu, menuModel };
    }

    getDefaultProductSize(productId: number, menu) {
        const productSizes = get(menu, `products[${productId}].product_sizes`, null);
        if (!productSizes) {
            return null;
        }

        const productSize = productSizes.find(size => size.dflt);

        if (productSize && !productSize.sold_out) {
            return productSize;
        }

        for (const productSize of productSizes) {
            if (!productSize.sold_out) {
                return productSize;
            }
        }

        return productSizes[0];
    }

    isAddButtonActive(productId: number): boolean {
        const { products, brand_id, outlet_id, menu_id, outlet_state } = this.menuModel.value;
        const isSoldOut = products[productId] && products[productId].sold_out;
        if (!products[productId]) {
            this.gtmService.logEvent('js_errors', {
                event_label: 'Product Not Found',
                event_value: `${brand_id}-${outlet_id}-${menu_id}-${productId}`
            });
            return false;
        } else {
            return (outlet_state === OutletState.ACTIVE || outlet_state === OutletState.PREORDER) && !isSoldOut;
        }
    }

    getButtonLabel(productId: number): string {
        const { products } = this.menuModel.value;
        const isSoldOut = products[productId].sold_out;

        // Currently always show 'ADD' (via disabled CTA) unless product is sold out
        if (isSoldOut) {
            return 'SOLD OUT';
        }

        return '';
    }

    getUpsellProducts() {
        return this.state.value.data.filter(product => product.upsell);
    }
}
